import React, { useEffect } from 'react';
import { withRouter, Redirect } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { reduxForm, getFormValues } from 'redux-form/immutable';
import { LinearProgress, CircularProgress } from '@material-ui/core';

import Box from 'retired/Box';
import { showErrorAction } from 'containers/Notification/actions';
import { PureRender } from 'utils/component-helpers';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import {
  selectAccessibleUserLocations,
  selectOrganization,
  selectUser,
} from 'entities/user/selectors';
import { selectBudgets } from 'entities/budget/selectors';
import Organization from 'entities/organization/model';
import BudgetAllocationType from 'entities/budgetAllocation/model';
import User from 'entities/user/model';
import { Features } from 'utils/features';
import { VOLUNTEER_REWARD_BUDGET_ALLOCATION } from 'entities/budgetAllocation/constants';
import Location from 'entities/location/model';

import reducer from './reducer';
import epics from './epics';
import { selectLoading } from './selectors';
import { FORM_IDENTIFIER } from './constants';
import * as Actions from './actions';
import BudgetAllocation from './BudgetAllocation';

const key = 'budgetAllocationFormPage';

interface BudgetAllocationFormPageProps {
  loading: boolean;
  pristine: boolean;
  invalid: boolean;
  submitting: boolean;
  actions: any;
  organization: Organization;
  match: any;
  history: any;
  budgetAllocation: BudgetAllocationType;
  handleSubmit: (
    submit: (formData: any) => void
  ) => ((event: React.FormEvent<HTMLFormElement>) => void) | undefined;
  budgets: any;
  user: User;
  budgetAllocationTypeName: string;
  budgetType: string;
  extraMatchingGrantsFields: boolean;
  locations: Iterable<string, Location>;
}
const BudgetAllocationsFormPage: React.FC<BudgetAllocationFormPageProps> = ({
  loading,
  pristine,
  invalid,
  submitting,
  actions,
  organization,
  handleSubmit,
  match,
  history,
  budgetAllocation,
  budgets,
  user,
  budgetAllocationTypeName = 'Corporate Donations',
  budgetType = '',
  extraMatchingGrantsFields,
  locations,
}) => {
  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  useEffect(() => {
    actions.initAction(match.params.id, match.params.budgetId);
  }, []);

  const submit = (formData) => {
    if (pristine) {
      history.replace('/donations/rules');
    } else {
      actions.saveAction(BudgetAllocationType.fromFormToRequestJson(formData.toJS()), budgetType);
    }
  };

  if (
    organization &&
    (!organization.isCompany() || !user.hasFeature(Features.CORPORATE_DONATIONS))
  ) {
    return <Redirect to="/donations" replace />;
  }

  const title = `${budgetAllocationTypeName} Budget Rule`;

  const budget = budgets?.find((b) => b.id === (budgetAllocation?.budget ?? match.params.budgetId));

  const requireStartAndEndDate = budgetType === VOLUNTEER_REWARD_BUDGET_ALLOCATION;

  return (
    <>
      {loading && <LinearProgress />}
      {!loading && (
        <Box>
          <Helmet title={title} />
          <h1>{title}</h1>
          {submitting ? (
            <CircularProgress />
          ) : (
            <form onSubmit={handleSubmit(submit)}>
              <div>
                <BudgetAllocation
                  budgetAllocation={budgetAllocation}
                  budget={budget}
                  pristine={pristine}
                  invalid={invalid}
                  remove={() => actions.removeAction(match.params.id, budgetType)}
                  budgetAllocationId={match.params.id}
                  extraMatchingGrantsFields={extraMatchingGrantsFields}
                  budgetAllocationTypeName={budgetAllocationTypeName}
                  suffixText={extraMatchingGrantsFields ? '' : ' and fundraisers'}
                  requireStartAndEndDate={requireStartAndEndDate}
                  locations={locations}
                />
              </div>
            </form>
          )}
        </Box>
      )}
    </>
  );
};

const getFormValuesSelector = getFormValues(FORM_IDENTIFIER);

const withConnect = connect(
  (state) => ({
    budgetAllocation: getFormValuesSelector(state),
    loading: selectLoading(state),
    organization: selectOrganization(state),
    budgets: selectBudgets(state),
    locations: selectAccessibleUserLocations(state),
    user: selectUser(state),
  }),
  (dispatch) => ({
    actions: bindActionCreators({ ...Actions, showErrorAction }, dispatch),
  })
);

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default compose(withRouter, withConnect, withForm, PureRender)(BudgetAllocationsFormPage);
